import { makeAutoObservable, runInAction } from "mobx";
import agent from "../api/agent";
import { Pagination, PagingParams } from "../models/pagination";
import {
  AccountsReceivable,
  ApplyARToOutstandingTicketFormValues,
  ApplyARToOutstandingTicketRequestDto,
  ApplyARToOutstandingTicketRequestItemDto,
  OutstandingTicketDto,
  QueueStatusDto,
} from "../models/accountsReceivable";
import { round2Decimals } from "../common/util/functions";

export default class AccountsReceivableStore {
  loadingInitial = false;
  loadingPayModal = false;
  loadingStatement = false;
  initiateCustomerPollingQueue = false;
  loadingQueueStatus = false;
  printCustomerStatementInitiated = false;
  printCustomerStatementRetryCount = 0;
  constructor() {
    makeAutoObservable(this);
  }

  setLoadingInitial = (state: boolean) => {
    this.loadingInitial = state;
  };
  setLoadingPayModal = (state: boolean) => {
    this.loadingPayModal = state;
  };
  setLoadingStatement = (state: boolean) => {
    this.loadingStatement = state;
  };
  setInitiateCustomerPollingQueue = (state: boolean) => {
    this.initiateCustomerPollingQueue = state;
  };
  setLoadingQueueStatus = (state: boolean) => {
    this.loadingQueueStatus = state;
  };
  setPrintCustomerStatementInitiated = (state: boolean) => {
    this.printCustomerStatementInitiated = state;
  }
  setPrintCustomerStatementRetryCount = (state: number) => {
    this.printCustomerStatementRetryCount = state;
  }
  customerARStatementObjectUrl: string | undefined = undefined;

  customerIdToLoad: string | undefined = undefined;
  paymentTicketsModalPagingParams = new PagingParams();
  paymentTicketsModalPagination: Pagination | null = null;
  paymentTicketsModalPredicate = new Map().set("all", true);
  
  paymentTicketsModalRegistry = new Map<string | undefined, OutstandingTicketDto>();
  paymentTicketsModalAR: AccountsReceivable | undefined;
  paymentTicketsModalTotal: number | undefined;

  printCustomerStatementQueueStatus: QueueStatusDto | undefined = undefined;

  // Inventory Index
  resetPaymentTicketsModalPredicate = () => {
    this.paymentTicketsModalPredicate.clear();
  }
  septPaymentTicketsModalPredicate = (predicate: string, value?: string) => {
    if (value)
      this.paymentTicketsModalPredicate.set(predicate, value);
    else
      this.paymentTicketsModalPredicate.delete(predicate);
  }
  setPaymentTicketsModalPagination = (pagination: Pagination | null) => {
    this.paymentTicketsModalPagination = pagination;
  }
  setPaymentTicketsModalPagingParams = (pagingParams: PagingParams) => {
    this.paymentTicketsModalPagingParams = pagingParams;
  }

  get paymentTicketsModalAxiosParams() {
    const params = new URLSearchParams();
    params.append("pageNumber", this.paymentTicketsModalPagingParams.pageNumber.toString());
    params.append("pageSize", this.paymentTicketsModalPagingParams.pageSize.toString());
    this.paymentTicketsModalPredicate.forEach((value, key) => params.append(key, value));

    return params;
  }

  setPaymentTicketsModalItem = (item: OutstandingTicketDto) => {
    this.paymentTicketsModalRegistry.set(item.ticketId, item);
  }
  get getPaymentTicketsModal() {
    return Array.from(this.paymentTicketsModalRegistry.values())
  }

  loadPaymentTicketsModal = async (customerId: string) => {
    this.loadingInitial = true;
    this.customerIdToLoad = customerId;
    this.paymentTicketsModalRegistry.clear();
    try {
      const result = await agent.accountsReceivable.getOutstandingTickets(customerId, this.paymentTicketsModalAxiosParams);
      runInAction(() => {
        result.data.items.forEach((dataItem: OutstandingTicketDto) => this.setPaymentTicketsModalItem(dataItem))
        this.paymentTicketsModalTotal = result.data.sum;
      });
      this.setPaymentTicketsModalPagination(result.pagination);
      return result;
    } catch (error) {
      console.log(error);
    } finally {
      runInAction(() => {
        this.loadingInitial = false;
      });
    }
  };
  loadAccountsReceivablePayModal = async (customerId: string) => {
    this.setLoadingPayModal(true);
    try {
      const result = await agent.customer.getAccountsReceivableByCustomerId(customerId);
      runInAction(() => {
        this.paymentTicketsModalAR = result;
      });
      return result;
    } catch (error) {
      console.log(error);
    } finally {
      runInAction(() => {
        this.setLoadingPayModal(false);
      });
    }
  };

  payForTickets = async (values: ApplyARToOutstandingTicketFormValues) => {
    this.setLoadingInitial(true);
    try {
      var applyARRequest = new ApplyARToOutstandingTicketRequestDto(values);
      await agent.accountsReceivable.payForTickets(applyARRequest);
    } finally {
      runInAction(() => {
        this.setLoadingInitial(false);
      });
    }
  }  

  setPrintCustomerStatementQueueStatus = (value: QueueStatusDto | undefined) => {
    this.printCustomerStatementQueueStatus = value;
  };
  queueCustomerAccountsReceivableStatement = async (customerId: string) => {
    this.setLoadingQueueStatus(true);
    try {
      const queueStatus = await agent.printAccountsReceivable.queueCustomerAccountsReceivableStatement(customerId);
      runInAction(() => {
        this.setPrintCustomerStatementQueueStatus(queueStatus);
      });
    } catch (error) {
      console.log(error);
    } finally {
      runInAction(() => {
        this.setLoadingQueueStatus(false);
        this.setPrintCustomerStatementInitiated(false);
      })
      return this.printCustomerStatementQueueStatus;
    }
  }
  printAccountsReceivableStatement = async (customerId: string) => {
    this.setLoadingStatement(true);
    try {
      await agent.printAccountsReceivable.printAccountsReceivableStatementForCustomer(customerId);
      runInAction(() => {
        if(this.initiateCustomerPollingQueue)
          this.setPrintCustomerStatementRetryCount(this.printCustomerStatementRetryCount + 1);
        else
          this.setPrintCustomerStatementRetryCount(0);

        this.printCustomerStatementInitiated = true;
      });
    } catch (error) {
      console.log(error);
    } finally {
      this.setLoadingStatement(false);
    }
  }

  applyPaymentsToOutstandingTickets = async (values:ApplyARToOutstandingTicketRequestDto) => {
    values.commonTicketPayment.createdDate = new Date();
    values.commonTicketPayment.amount = round2Decimals(Number(values.commonTicketPayment.amount ?? 0));
    values.commonTicketPayment.refunded = false;
    values.commonTicketPayment.refundedAmount = 0.0;
  }


  resetPaymentTicketsModalRegistry = () => {
    this.paymentTicketsModalRegistry.clear();
  }

  calculateTotalPaymentTicketDue = (payments: OutstandingTicketDto[]) => {
    return payments.reduce((total, p) => total += p.balanceDue, 0)
  }
  
  calculateAmountToPay = (payments: ApplyARToOutstandingTicketRequestItemDto[]) => {
    return payments.reduce((total, p) => total += (p.amountToPay ?? 0), 0)
  }
}
